import React, { useState, useEffect, useRef } from 'react';
import type { DonationProduct, DonationOption } from '@/lib/TicketApi';
import { shopVar } from '@/lib/shopStyles';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';
import { useCart } from '@/providers/CartProvider';

interface DonationProductProps {
    product: DonationProduct;
}

const DonationProduct: React.FC<DonationProductProps> = ({ product }) => {
    const { currentLanguage: lang } = useLanguage();
    const { setProductQuantity, cart } = useCart();
    const [selected, setSelected] = useState<number | 'custom' | null>(null);
    const [customAmount, setCustomAmount] = useState<string>('');
    const [validationError, setValidationError] = useState<string | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const options: DonationOption[] = product.donation.options || [];
    const custom = product.donation.customDonation != null;
    const minAmount = product.donation.customDonation?.lowerLimit.amount ?? 0;
    const maxAmount = product.donation.customDonation?.upperLimit.amount ?? Infinity;

    // Parse euro input to cents
    function parseEuroInputToCents(input: string): number {
        if (!input) return 0;
        const normalized = input.replace(',', '.');
        const parts = normalized.split('.');
        let safe = parts[0];
        if (parts.length > 1) {
            safe += '.' + parts.slice(1).join('');
        }
        if (safe.includes('.')) {
            const floatVal = parseFloat(safe);
            if (isNaN(floatVal)) return 0;
            return Math.round(floatVal * 100);
        } else {
            const intVal = parseInt(safe, 10);
            if (isNaN(intVal)) return 0;
            return intVal * 100;
        }
    }

    // Sync with cart on mount or cart change
    useEffect(() => {
        if (!cart) return;
        const cartProduct = cart.products.find(p => p.productId === product.productId);
        if (!cartProduct || !cartProduct.settings) {
            setSelected(null);
            setCustomAmount('');
            setValidationError(null);
            return;
        }
        const { amount, currency } = cartProduct.settings;
        if (typeof amount !== 'number' || !currency) {
            setSelected(null);
            setCustomAmount('');
            setValidationError(null);
            return;
        }
        // Try to find a matching option
        const idx = options.findIndex(opt => opt.amount === amount && opt.currency === currency);
        if (idx !== -1) {
            setSelected(idx);
            setCustomAmount('');
            setValidationError(null);
        } else {
            setSelected('custom');
            // Format cents to euro string for display
            const euros = (amount / 100).toLocaleString(lang === 'nl' ? 'nl-NL' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const euroString = euros.replace('.', ',');
            setCustomAmount(euroString);
            setValidationError(null);
        }
    }, [cart, product.productId, options, lang]);

    // Handle option select
    const handleSelect = (idx: number | 'custom') => {
        if (selected === idx) {
            setProductQuantity(product.productId, 0);
            setSelected(null);
            setCustomAmount('');
            setValidationError(null);
            return;
        }
        setSelected(idx);
        setValidationError(null);
        if (idx === 'custom') {
            setCustomAmount('');
            // Focus input after render
            setTimeout(() => inputRef.current?.focus(), 0);
            return;
        }
        const option = options[idx as number];
        setProductQuantity(product.productId, 1, {
            amount: option.amount,
            currency: option.currency
        });
        setCustomAmount('');
    };

    // Handle custom input change
    const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^0-9.,]/g, '');
        const parts = value.split(/[.,]/);
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }
        setCustomAmount(value);
        setValidationError(null);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            const cents = parseEuroInputToCents(value);
            if (!value || cents <= 0) {
                setProductQuantity(product.productId, 0);
                setValidationError(null);
                return;
            }
            if (cents < minAmount) {
                setValidationError(`Minimum amount is €${(minAmount / 100).toFixed(2)}`);
                setProductQuantity(product.productId, 0);
                return;
            }
            if (cents > maxAmount) {
                setValidationError(`Maximum amount is €${(maxAmount / 100).toFixed(2)}`);
                setProductQuantity(product.productId, 0);
                return;
            }
            setValidationError(null);
            setProductQuantity(product.productId, 1, {
                amount: cents,
                currency: product.price.currency
            });
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        };
    }, []);

    const hasDescription = product.description && product.description.trim() !== '';

    return (
        <div
            className="border rounded-md px-4 pt-4 pb-2 mb-4 shadow-md last:mb-0 overflow-hidden"
            style={{ borderColor: shopVar('--shop-borderColor') }}
        >
            <div className="flex items-start border-b border-dashed w-full pb-4" style={{ borderColor: shopVar('--shop-borderColor') }}>
                <div className="flex-grow pr-2">
                    <div className="flex items-center">
                        <h2 className="text-base font-bold mr-2" style={{ color: shopVar('--shop-textColor') }}>{product.name}</h2>
                    </div>
                    {hasDescription && (
                        <p className="font-normal text-xs tracking-tight mt-1" style={{ color: shopVar('--shop-textColor') }}>
                            {product.description}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
                {options.map((opt: DonationOption, idx: number) => (
                    <button
                        key={idx}
                        className={`flex-1 min-w-[80px] max-w-[160px] px-4 py-2 rounded border text-center font-semibold transition-colors ${selected === idx ? 'ring-2 ring-black' : ''}`}
                        style={{
                            background: selected === idx ? shopVar('--shop-addToCartButtonColor') : shopVar('--shop-panelBackgroundColor'),
                            color: selected === idx ? shopVar('--shop-addToCartButtonTextColor') : shopVar('--shop-textColor'),
                            borderColor: shopVar('--shop-borderColor'),
                        }}
                        onClick={() => handleSelect(idx)}
                        type="button"
                    >
                        {Money.fromInteger(opt.amount, opt.currency).display(lang)}
                    </button>
                ))}
                {custom && (
                    <button
                        className={`flex-1 min-w-[80px] max-w-[160px] px-4 py-2 rounded border text-center font-semibold transition-colors ${selected === 'custom' ? 'ring-2 ring-black' : ''}`}
                        style={{
                            background: selected === 'custom' ? shopVar('--shop-addToCartButtonColor') : shopVar('--shop-panelBackgroundColor'),
                            color: selected === 'custom' ? shopVar('--shop-addToCartButtonTextColor') : shopVar('--shop-textColor'),
                            borderColor: shopVar('--shop-borderColor'),
                        }}
                        onClick={() => handleSelect('custom')}
                        type="button"
                    >
                        Choose custom
                    </button>
                )}
            </div>
            {selected === 'custom' && (
                <div className="mt-4">
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={customAmount}
                            onChange={handleCustomAmountChange}
                            placeholder="Enter amount in euros"
                            className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 placeholder:text-[color:var(--shop-textColor)] placeholder:opacity-60"
                            style={{
                                color: shopVar('--shop-textColor'),
                                backgroundColor: shopVar('--shop-panelBackgroundColor'),
                                // @ts-expect-error --tw-ring-color is not a valid CSS property
                                '--tw-ring-color': shopVar('--shop-borderColor'),
                            }}
                        />
                    </div>
                    {validationError && (
                        <div className="text-red-600 text-xs mt-1">{validationError}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DonationProduct; 